import { BIBLE_BOOKS, chapterIdFor } from './bibleBooks';

/**
 * Parse a free-text reading reference into BibleChapter ids.
 *
 * Handles what someone actually types into a box that says "I read…":
 *
 *   gen 1-5          → Genesis 1,2,3,4,5
 *   john 3           → John 3
 *   ps 23, 24        → Psalm 23, Psalm 24
 *   1 sam 1-3        → 1 Samuel 1,2,3
 *   matthew          → all 28 chapters of Matthew
 *   gen 1-3; ex 2    → both
 *
 * Returns { chapters, errors }. `chapters` is always a valid, de-duplicated,
 * canonically ordered list, so a partly-understood line still logs the part we
 * did understand and reports the rest.
 */

// "1", "i", "first" at the start of a book name all mean the same thing.
// A digit needs no separator (no book starts with one), but a word ordinal
// does — otherwise the "i" in "isaiah" would read as a leading 1.
const ORDINAL_DIGIT = /^([123])\s*/;
const ORDINAL_WORD = /^(iii|ii|i|first|second|third)\s+/;
const ORDINAL_VALUES = {
  i: '1', ii: '2', iii: '3',
  first: '1', second: '2', third: '3'
};

/**
 * Lowercase, drop punctuation and spaces, and normalise a leading ordinal, so
 * "1 Sam.", "I Samuel" and "1sam" all collapse to "1sam".
 */
export function normalizeBookName(raw) {
  let name = String(raw || '')
    .toLowerCase()
    .replace(/[.'’]/g, '')
    .trim();

  const digitMatch = name.match(ORDINAL_DIGIT);
  if (digitMatch) {
    name = digitMatch[1] + name.slice(digitMatch[0].length);
  } else {
    const wordMatch = name.match(ORDINAL_WORD);
    if (wordMatch) {
      name = ORDINAL_VALUES[wordMatch[1]] + name.slice(wordMatch[0].length);
    }
  }

  return name.replace(/\s+/g, '');
}

const NORMALIZED_BOOKS = BIBLE_BOOKS.map(book => ({
  ...book,
  normalized: normalizeBookName(book.name)
}));

/**
 * Resolve a typed book name to exactly one book.
 *
 * Exact name and exact alias win outright; otherwise we accept a prefix only
 * when it matches a single book, so "jo" is reported as ambiguous rather than
 * silently logging Job.
 *
 * @returns {{book: Object}|{error: string}}
 */
export function resolveBook(raw) {
  const name = normalizeBookName(raw);
  if (!name) return { error: 'Which book?' };

  const exact = NORMALIZED_BOOKS.find(book => book.normalized === name);
  if (exact) return { book: exact };

  const alias = NORMALIZED_BOOKS.find(book => book.aliases.includes(name));
  if (alias) return { book: alias };

  const prefixed = NORMALIZED_BOOKS.filter(book => book.normalized.startsWith(name));
  if (prefixed.length === 1) return { book: prefixed[0] };
  if (prefixed.length > 1) {
    const names = prefixed.slice(0, 4).map(b => b.name).join(', ');
    return { error: `"${raw.trim()}" could be ${names}${prefixed.length > 4 ? ', …' : ''}. Which one?` };
  }

  return { error: `We don't know a book called "${raw.trim()}".` };
}

// A book name (letters, spaces, optional leading number) followed by an
// optional chapter list: "1 sam 1-3, 5" / "matthew".
const SEGMENT_PATTERN = /^\s*((?:[123]|i{1,3}|first|second|third)?\s*[a-z][a-z\s.'’]*?)\s*([\d\s,–—-]*)$/i;

/**
 * Split "gen 1-3; ex 2 and john 1" into per-book segments.
 * Commas are ambiguous (they separate chapters *and* books), so a comma only
 * starts a new segment when what follows begins with a letter.
 */
function splitSegments(input) {
  return String(input)
    .split(/\s*(?:;|\+|\band\b|,(?=\s*(?:[123]|i{1,3})?\s*[a-z]))\s*/i)
    .map(part => part.trim())
    .filter(Boolean);
}

/**
 * Parse a chapter list like "1-3, 5, 7" into numbers.
 * @returns {{chapters: number[]}|{error: string}}
 */
function parseChapterList(text, book) {
  const chapters = [];

  for (const piece of text.split(',').map(p => p.trim()).filter(Boolean)) {
    // en/em dashes get typed as often as hyphens
    const range = piece.match(/^(\d+)\s*[-–—]\s*(\d+)$/);
    if (range) {
      const start = parseInt(range[1], 10);
      const end = parseInt(range[2], 10);
      if (start > end) {
        return { error: `${book.name} ${start}-${end} runs backwards.` };
      }
      if (end > book.chapters) {
        return { error: `${book.name} only has ${book.chapters} chapters.` };
      }
      for (let chapter = start; chapter <= end; chapter++) chapters.push(chapter);
      continue;
    }

    if (!/^\d+$/.test(piece)) {
      return { error: `We couldn't read "${piece}" as a chapter.` };
    }

    const chapter = parseInt(piece, 10);
    if (chapter < 1 || chapter > book.chapters) {
      return { error: `${book.name} only has ${book.chapters} chapters.` };
    }
    chapters.push(chapter);
  }

  return { chapters };
}

/**
 * Parse free text into chapter ids.
 *
 * @param {string} input
 * @returns {{chapters: Array<{chapterId: number, book: string, chapter: number}>, errors: string[]}}
 */
export function parseReference(input) {
  const errors = [];
  const byId = new Map();

  const text = String(input || '').trim();
  if (!text) return { chapters: [], errors: [] };

  for (const segment of splitSegments(text)) {
    const match = segment.match(SEGMENT_PATTERN);
    if (!match) {
      errors.push(`We couldn't read "${segment}".`);
      continue;
    }

    const [, bookText, chapterText] = match;
    const resolved = resolveBook(bookText);
    if (resolved.error) {
      errors.push(resolved.error);
      continue;
    }

    const { book } = resolved;
    const trimmedChapters = (chapterText || '').trim();

    // A bare book name means the whole book.
    const chapterNumbers = trimmedChapters
      ? parseChapterList(trimmedChapters, book)
      : { chapters: Array.from({ length: book.chapters }, (_, i) => i + 1) };

    if (chapterNumbers.error) {
      errors.push(chapterNumbers.error);
      continue;
    }

    for (const chapter of chapterNumbers.chapters) {
      const chapterId = chapterIdFor(book, chapter);
      if (chapterId && !byId.has(chapterId)) {
        byId.set(chapterId, { chapterId, book: book.name, chapter });
      }
    }
  }

  const chapters = Array.from(byId.values()).sort((a, b) => a.chapterId - b.chapterId);
  return { chapters, errors };
}

/**
 * Collapse parsed chapters into display chips: consecutive chapters of the
 * same book become one "Genesis 1-5" chip.
 */
export function toChips(chapters) {
  const chips = [];

  for (const entry of chapters) {
    const last = chips[chips.length - 1];
    if (last && last.book === entry.book && entry.chapter === last.end + 1) {
      last.end = entry.chapter;
      last.chapterIds.push(entry.chapterId);
      continue;
    }
    chips.push({
      book: entry.book,
      start: entry.chapter,
      end: entry.chapter,
      chapterIds: [entry.chapterId]
    });
  }

  return chips.map(chip => ({
    ...chip,
    label: chip.start === chip.end
      ? `${chip.book} ${chip.start}`
      : `${chip.book} ${chip.start}-${chip.end}`
  }));
}

export default parseReference;
